import { useLocation } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { useGetAllServicesHealthQuery, useScheduledWorkAttentionCountQuery } from '../../api/client'
import { sidebarOpenState } from '../../store/DomainStore'
import ActiveNavIndicator from './ActiveNavIndicator'
import NavigationItem from './NavigationItem'
import { ADMIN_NAV_GROUPS, getAdminNavigationItems } from './admin-navigation-data'
import { whichPageAreWeOn } from './page-name'

/**
 * The admin secondary navigation — the grouped list of admin functions that used
 * to live inline in the main rail. It renders inside AdminLayout and shares the
 * main rail's open/collapsed state, so the whole admin surface collapses to an
 * icon rail together. Each group is an eyebrow (the "Admin" title lives in the
 * panel header above), and one item is highlighted via the shared indicator.
 */
export default function AdminSidebar() {
    const [sidebarOpen] = useRecoilState(sidebarOpenState)
    const location = useLocation()
    const paths = location.pathname.split('/')
    const pages = whichPageAreWeOn(paths)

    // The needs-attention count: live schedules whose last run failed. On
    // the nav itself so service delivery sees failures from anywhere in the
    // admin area, without remembering to visit the jobs page.
    const { data: attentionData } = useScheduledWorkAttentionCountQuery({
        pollInterval: 60000,
        errorPolicy: 'ignore'
    })
    const attentionCount = attentionData?.scheduledWorkAttentionCount ?? 0

    // Same idea for services: a service whose heartbeat has stopped is an
    // outage nobody sees until someone opens the page, so the count of
    // critical services rides on the nav. There is no server-side count to
    // call, so we reuse the page's own query — Apollo shares the cached
    // result with the page when it is open, so this costs one extra call a
    // minute, not two.
    const { data: serviceHealthData } = useGetAllServicesHealthQuery({
        pollInterval: 60000,
        errorPolicy: 'ignore'
    })
    // Critical only. Warning means "late", which the page colours amber and
    // which would leave the badge permanently lit for services that are
    // merely slow.
    const criticalServiceCount = (serviceHealthData?.getAllServicesHealth?.items ?? []).filter(
        (service) => service?.healthStatus === 'CRITICAL'
    ).length

    // Counts by nav item id, so adding another badged item is one entry here
    // rather than another branch in the map below.
    const badgeCounts: Record<string, { count: number; label: string }> = {
        'scheduled-jobs': { count: attentionCount, label: 'failing' },
        'service-health': { count: criticalServiceCount, label: 'critical' }
    }

    const items = getAdminNavigationItems(pages).map((item) => {
        const id = 'id' in item ? item.id : undefined
        const badge = id ? badgeCounts[id] : undefined
        return badge && badge.count > 0
            ? { ...item, badge: badge.count, badgeLabel: badge.label }
            : item
    })

    // Re-measure the active-item indicator on route or collapse changes.
    const navSignature = [location.pathname, sidebarOpen].join('|')

    return (
        <nav className="relative px-3 pb-4 pt-2">
            <ActiveNavIndicator signature={navSignature} />
            <div className="space-y-0.5">
                {ADMIN_NAV_GROUPS.map((group, groupIndex) => {
                    const groupItems = items.filter((item) => item.group === group.id)
                    if (groupItems.length === 0) {
                        return null
                    }
                    return (
                        <div key={group.id}>
                            {sidebarOpen ? (
                                <div className="px-3 pb-1 pt-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-400">
                                    {group.label}
                                </div>
                            ) : (
                                // Collapsed: no room for labels, so a quiet rule keeps
                                // the icon clusters grouped. Skip it before the first.
                                groupIndex > 0 && <hr className="my-3 border-ink-100" />
                            )}
                            {groupItems.map((item) => (
                                <NavigationItem
                                    item={item}
                                    sidebarOpen={sidebarOpen}
                                    key={item.name}
                                />
                            ))}
                        </div>
                    )
                })}
            </div>
        </nav>
    )
}
